import React from 'react';
import { X } from 'lucide-react';
import { VoiceRecordingErrorBoundary } from './VoiceRecordingErrorBoundary';
import { cn } from '../../lib/utils';
import { useViewport } from '../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';

interface VoiceRecorderProps {
  isRecording: boolean;
  recordingTime: number;
  maxRecordingTime?: number;
  onCancel: () => void;
}

export function VoiceRecorder({
  isRecording,
  recordingTime,
  maxRecordingTime = 300, // 5 minutes default
  onCancel,
}: VoiceRecorderProps) {
  const { isMobile } = useViewport();
  const { isNativeApp, navigationBarHeight } = useStatusBarInfo();

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isRecording) return null;

  const handleError = (error: Error) => {
    console.error('Voice recording error caught by boundary:', error);
    onCancel(); // Cancel recording on error
  };

  return (
    <VoiceRecordingErrorBoundary onError={handleError}>
      <div className="relative">
        <div
          className={cn(
            "absolute left-1/2 -translate-x-1/2 z-50 w-[90vw] sm:w-auto max-w-[400px]",
            isMobile && isNativeApp ? "bottom-[72px]" : "-top-16"
          )}
          style={{
            bottom: isMobile && isNativeApp
              ? `calc(72px + ${navigationBarHeight}px)`
              : undefined
          }}
        >
          <div
            className={cn(
              'bg-white rounded-full border border-gray-200 shadow-lg',
              'flex items-center justify-between w-full',
              'px-4 sm:px-6 py-2 gap-2 sm:gap-4'
            )}
          >
            {/* Recording indicator */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-medium text-gray-900">Recording</span>
            </div>

            {/* Timer */}
            <div className="bg-gray-100 px-2 py-1 rounded-full flex-shrink-0">
              <span className="text-sm font-medium text-gray-900">
                {formatTime(recordingTime)} / {formatTime(maxRecordingTime)}
              </span>
            </div>

            {/* Cancel button - now inside div with proper spacing */}
            <button
              type="button"
              onClick={onCancel}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </VoiceRecordingErrorBoundary>
  );
}
